"use client";

import * as React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { isApiError, type SiteUser, type SiteUserProfile } from "@gerama/types";
import { useSiteAuthStore } from "@gerama/stores";
import { browserCoreApi } from "@/lib/api";

export const profileKeys = {
  me: (userId: number | null) => ["users", "me", userId] as const,
};

/** Image types `POST /users/me/avatar` accepts — mirrors the backend's `USER_AVATAR_EXTENSIONS`. */
export const AVATAR_ACCEPT = "image/jpeg,image/png,image/webp,image/gif,image/avif";
export const AVATAR_MAX_BYTES = 5 * 1024 * 1024;

function errorMessage(error: unknown, fallback: string): string {
  return isApiError(error) && error.message ? error.message : fallback;
}

/**
 * `GET /users/me` for the signed-in listener — the fresh profile plus the plan
 * badge. Every read is copied into the session store, so the header avatar and
 * name stay current (e.g. a Google picture picked up on a later sign-in)
 * without a re-login. Disabled while signed out.
 */
export function useMyProfile() {
  const token = useSiteAuthStore((s) => s.token);
  const userId = useSiteAuthStore((s) => s.user?.id ?? null);
  const setUser = useSiteAuthStore((s) => s.setUser);

  const query = useQuery({
    queryKey: profileKeys.me(userId),
    queryFn: ({ signal }) => browserCoreApi.users.me(signal),
    enabled: Boolean(token),
    staleTime: 5 * 60_000,
  });

  React.useEffect(() => {
    if (query.data) setUser(query.data);
  }, [query.data, setUser]);

  return query;
}

/** Writes an edited profile into the query cache (keeping the plan badge) and the session store. */
function useApplyProfile() {
  const client = useQueryClient();
  const setUser = useSiteAuthStore((s) => s.setUser);
  return React.useCallback(
    (user: SiteUser) => {
      client.setQueryData<SiteUserProfile>(profileKeys.me(user.id), (previous) => ({
        ...user,
        plan: previous?.plan ?? null,
      }));
      setUser(user);
    },
    [client, setUser],
  );
}

export function useUpdateProfile() {
  const apply = useApplyProfile();
  return useMutation({
    mutationFn: (name: string) => browserCoreApi.users.updateMe({ name }),
    onSuccess: (user) => {
      apply(user);
      toast.success("نام شما ذخیره شد.");
    },
    onError: (error) => toast.error(errorMessage(error, "ذخیرهٔ نام انجام نشد.")),
  });
}

export function useUploadAvatar() {
  const apply = useApplyProfile();
  return useMutation({
    mutationFn: (file: File) => browserCoreApi.users.uploadAvatar(file),
    onSuccess: (user) => {
      apply(user);
      toast.success("تصویر پروفایل به‌روز شد.");
    },
    onError: (error) => toast.error(errorMessage(error, "بارگذاری تصویر انجام نشد.")),
  });
}

export function useRemoveAvatar() {
  const apply = useApplyProfile();
  return useMutation({
    mutationFn: () => browserCoreApi.users.removeAvatar(),
    onSuccess: (user) => {
      apply(user);
      toast.success("تصویر پروفایل حذف شد.");
    },
    onError: (error) => toast.error(errorMessage(error, "حذف تصویر انجام نشد.")),
  });
}
